import { Task } from 'app/data/types/tasks'
import { createWithEqualityFn } from 'zustand/traditional'

/**
 * TaskDialogStore Interface
 *
 * Defines the shape of the task dialog state and its actions.
 * Used to prevent multiple instances of the task dialog from being opened
 * and to manage the dialog's state globally.
 */
interface TaskDialogStore {
  visible: boolean // Single source of truth for visibility
  editingTask: Task | null
  defaultFocusDate: string | null

  show: (task?: Task, focusDate?: string) => void
  hide: () => void
}

/**
 * Task Dialog Store
 *
 * Global state management for the task dialog component.
 * Handles opening/closing the dialog and prevents multiple instances
 * from being opened simultaneously.
 */
export const useTaskDialogStore = createWithEqualityFn<TaskDialogStore>((set) => ({
  visible: false,
  editingTask: null,
  defaultFocusDate: null,

  /**
   * Shows the task dialog for either creating or editing.
   * 
   * @param task - If provided, we're EDITING this existing task. If null/undefined, we're CREATING a new task.
   * @param focusDate - Default date to pre-fill for new tasks (only used when task is null/undefined)
   */
  show: (task?: Task, focusDate?: string) => {
    set({
      visible: true,
      editingTask: task ?? null, // null = create new, Task = edit existing
      defaultFocusDate: focusDate ?? null,
    })
  },

  hide: () => {
    // Completely reset state when hiding the dialog
    // This ensures no persisted values leak between dialog instances
    set({
      visible: false,
      editingTask: null,
      defaultFocusDate: null,
    })
  },
}))
